/**
 * Pure search helpers for the My Library and Catalog screens. Two scopes:
 *   - "titles": match catalog/pack titles, ids, and tags (instant, no I/O).
 *   - "content": additionally match inside lesson text (the screens load each pack
 *     and feed lessons through {@link matchLesson}).
 * Framework-free so it's unit-tested; the I/O orchestration lives in src/ui.
 */
import { CatalogPackEntry } from "../core/catalog";
import { CatalogNode } from "./catalogTree";
import { foldMatchText } from "./findMatches";

/**
 * Lowercase, trim, and strip diacritics (é → e, ñ → n, …) via Unicode NFD decomposition
 * + combining-mark removal, so "café" matches a "cafe" query and vice versa. Built on
 * `findMatches.ts`'s `foldMatchText` (the same fold docs/135-IN-LESSON-SEARCH.md's
 * in-lesson find engine mirrors) — one fold, so list search and in-lesson find always
 * agree on what "matches."
 */
export function normalizeQuery(q: string): string {
  return foldMatchText(q.trim());
}

/** Title/id/tag match for a pack entry. An empty query matches everything. */
export function packMatchesQuery(
  entry: { id: string; title: string; tags?: string[] },
  q: string,
): boolean {
  const n = normalizeQuery(q);
  if (!n) return true;
  if (entry.title.toLowerCase().includes(n)) return true;
  if (entry.id.toLowerCase().includes(n)) return true;
  return (entry.tags ?? []).some((t) => t.toLowerCase().includes(n));
}

/**
 * Plain substring match against a single piece of text (case-insensitive query,
 * matched as-is against the text). An empty query matches everything. Used to extend
 * My Library's title search to corpora that aren't `CatalogPackEntry`s — Recents
 * (match either `packTitle` or `lessonTitle`) and saved-catalog titles — without
 * dragging in `packMatchesQuery`'s id/tag fields, which those types don't have.
 */
export function titleMatches(text: string, q: string): boolean {
  const n = normalizeQuery(q);
  if (!n) return true;
  return text.toLowerCase().includes(n);
}

/** Whether a node still has anything to show. */
export function nodeHasContent(node: CatalogNode): boolean {
  return node.packs.length > 0 || node.children.length > 0;
}

/**
 * Prune a catalog tree to titles/tags matching the query. A catalog whose own title
 * matches is kept whole (so you see its contents); otherwise only matching packs and
 * non-empty sub-catalogs remain. An empty query returns the tree unchanged.
 */
export function filterCatalogTree(node: CatalogNode, q: string): CatalogNode {
  const n = normalizeQuery(q);
  if (!n) return node;
  if (node.title && node.title.toLowerCase().includes(n)) return node;
  const children = node.children.map((c) => filterCatalogTree(c, q)).filter(nodeHasContent);
  const packs = node.packs.filter((p) => packMatchesQuery(p, q));
  return { ...node, packs, children };
}

/** Every pack entry reachable under a node (depth-first), for content search. */
export function flattenPacks(node: CatalogNode): CatalogPackEntry[] {
  return [...node.packs, ...node.children.flatMap(flattenPacks)];
}

/** A content match within one lesson. */
export interface LessonHit {
  lessonId: string;
  lessonTitle: string;
  snippet: string;
}

/** A short context snippet around the first match (collapsed whitespace, ellipsized). */
export function makeSnippet(text: string, q: string, radius = 40): string {
  const n = normalizeQuery(q);
  const flat = text.replace(/\s+/g, " ").trim();
  const idx = flat.toLowerCase().indexOf(n);
  if (idx < 0) return flat.slice(0, radius * 2).trim();
  const start = Math.max(0, idx - radius);
  const end = Math.min(flat.length, idx + n.length + radius);
  return (start > 0 ? "…" : "") + flat.slice(start, end).trim() + (end < flat.length ? "…" : "");
}

/** Match one lesson's title + body markdown against the query → a hit, or null. */
export function matchLesson(
  lesson: { id: string; title: string },
  markdown: string,
  q: string,
): LessonHit | null {
  const n = normalizeQuery(q);
  if (!n) return null;
  const inTitle = lesson.title.toLowerCase().includes(n);
  const inBody = markdown.toLowerCase().includes(n);
  if (!inTitle && !inBody) return null;
  return {
    lessonId: lesson.id,
    lessonTitle: lesson.title,
    snippet: inBody ? makeSnippet(markdown, q) : lesson.title,
  };
}
